import { creatorbase } from '@round/api';
import { Combobox, Skeleton } from '@round/creatorbase-ui';
import cn from 'classnames';
import PoolChips from './components/PoolChips/PoolChips';
import PoolOption from 'Modules/Plans/CreatorPools/components/PoolOption/PoolOption';
import { PoolItem, usePoolSearchCombobox } from 'Modules/Plans/CreatorPools/components/usePoolSearchCombobox';
import styles from './PoolsRow.module.css';

const SKELETON_CHIP_COUNT = 2;

type Props = {
    pools: creatorbase.Pool[];
    isLoading: boolean;
    onAdd: (pool: creatorbase.Pool) => void;
    onRemove: (pool: creatorbase.Pool) => void;
    className?: string;
};

const PoolsRow = ({ pools, isLoading, onAdd, onRemove, className }: Props) => {
    const combobox = usePoolSearchCombobox({ onSelect: onAdd, memberPoolIds: pools.map(({ id }) => id) });

    // The endpoint orders pools by name. An optimistically added pool is appended, so without
    // sorting here its chip sits last until the refetch lands and then jumps into place.
    const sortedPools = [...pools].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div className={cn(styles.row, className)}>
            <p className={styles.label}>Pools</p>

            {isLoading ? (
                <div className={styles.skeletonChips}>
                    {Array.from({ length: SKELETON_CHIP_COUNT }, (_, index) => (
                        <Skeleton key={index} className={styles.skeletonChip} />
                    ))}
                </div>
            ) : (
                <PoolChips pools={sortedPools} onRemove={onRemove} />
            )}

            <div className={styles.addPool}>
                <Combobox
                    {...combobox.rootProps}
                    placeholder="Add to pool"
                    disabled={isLoading}
                    noItemsFound={combobox.noItemsFound}
                    onItemsListScroll={combobox.onItemsListScroll}
                    renderItem={(item: PoolItem) => (
                        <PoolOption
                            name={item.label}
                            description={item.description}
                            memberCount={item.member_count}
                            isMember={combobox.isMember(item.value)}
                        />
                    )}
                />
            </div>
        </div>
    );
};

export default PoolsRow;
